import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { repository } from '../../data'
import { imageUrl } from '../imageFile'
import { AddPhotoButton } from './AddPhotoButton'
import { ItemImage } from './ItemArt'
import { useCan } from './shared'

/**
 * The item page's pictures: one large, the rest as thumbnails under it, and
 * Add photo right there. The first photo is the cover — the one on the tile,
 * in Receive and in every delivery row — and any other can be made the cover.
 *
 * With none yet, the drawing stands in at full size with the button on it,
 * because that is the moment a photo is most worth taking.
 */
export function ItemGallery({ itemId, name, category }: { itemId: string; name: string; category: string }): JSX.Element {
  const client = useQueryClient()
  const can = useCan()
  const key = ['attachments', 'component_type', itemId]
  const photos = useQuery({ queryKey: key, queryFn: () => repository.attachments('component_type', itemId) })
  const [selected, setSelected] = useState(0)
  const [zoom, setZoom] = useState(false)

  const refresh = async (): Promise<void> => {
    setSelected(0)
    for (const k of [key, ['inventory'], ['inventory-item', itemId], ['receipts']]) await client.invalidateQueries({ queryKey: k })
  }
  const cover = useMutation({ mutationFn: (id: string) => repository.makePrimaryAttachment(id), onSuccess: refresh })
  const remove = useMutation({ mutationFn: (id: string) => repository.deleteAttachment(id), onSuccess: refresh })

  const list = (photos.data ?? []).filter((a) => a.purpose === 'photo')
  const current = list[Math.min(selected, list.length - 1)] ?? null

  if (!current) {
    return (
      <div className="inv-gallery">
        <div className="inv-gallery__main inv-gallery__main--empty">
          <ItemImage imageId={null} category={category} name={name} />
          <span className="inv-gallery__prompt">
            <span>No photo yet — add one so anyone can recognise it on the shelf.</span>
            <AddPhotoButton itemId={itemId} label="Add photo" primary className="inv-gallery__add" />
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="inv-gallery">
      <button type="button" className="inv-gallery__main" onClick={() => setZoom(true)} title="Show larger">
        <img src={imageUrl(current.id)} alt={name} />
      </button>
      {list.length > 1 ? (
      <div className="inv-gallery__strip">
        {list.map((p, i) => (
          <button
            key={p.id}
            type="button"
            className="inv-gallery__thumb"
            aria-pressed={p.id === current.id}
            aria-label={`Photo ${i + 1}${i === 0 ? ', the cover' : ''}`}
            onClick={() => setSelected(i)}
          >
            <img src={imageUrl(p.id)} alt="" loading="lazy" />
          </button>
        ))}
      </div>
      ) : null}
      <div className="inv-gallery__actions">
        <AddPhotoButton itemId={itemId} label="Add photo" />
        {current.id !== list[0]?.id ? (
          <button type="button" onClick={() => cover.mutate(current.id)} disabled={cover.isPending}>
            Make cover
          </button>
        ) : null}
        {can('delete_attachment') ? (
          <button type="button" className="danger" onClick={() => window.confirm('Delete this photo? It is removed everywhere it is shown.') && remove.mutate(current.id)} disabled={remove.isPending}>
            Delete photo
          </button>
        ) : null}
      </div>
      {zoom ? (
        <div className="lightbox" role="dialog" aria-label={name} onClick={() => setZoom(false)}>
          <img src={imageUrl(current.id)} alt={name} />
        </div>
      ) : null}
    </div>
  )
}
